import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { ROLE_LABELS, isAdmin } from '../lib/rbac';
import type { AppRole } from '../lib/database.types';
import {
  fmtMinutes,
  loadUsageReport,
  usageStatus,
  usageToCsv,
  type UsageRow,
  type UsageStatus,
} from '../lib/usage';

const PERIODS = [7, 30, 90];
const INACTIVE_OPTIONS = [7, 14, 30];
type Filter = 'todos' | UsageStatus;

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Adesao() {
  const { currentOrgId, grants } = useAuth();
  const [days, setDays] = useState(30);
  const [inactiveDays, setInactiveDays] = useState(14);
  const [filter, setFilter] = useState<Filter>('todos');
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const allowed = Boolean(grants && isAdmin(grants));

  useEffect(() => {
    if (!currentOrgId || !allowed) return;
    let cancelled = false;
    setLoading(true);
    loadUsageReport(currentOrgId, days)
      .then((r) => {
        if (cancelled) return;
        setRows(r);
        setErr(null);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [currentOrgId, days, allowed]);

  const withStatus = useMemo(
    () => rows.map((r) => ({ r, status: usageStatus(r, inactiveDays) })),
    [rows, inactiveDays],
  );
  const counts = useMemo(() => {
    const c = { nunca: 0, inativo: 0, ativo: 0 };
    withStatus.forEach((x) => (c[x.status] += 1));
    return c;
  }, [withStatus]);
  const shown = withStatus.filter((x) => filter === 'todos' || x.status === filter);

  function exportCsv() {
    const blob = new Blob([usageToCsv(shown.map((x) => x.r), inactiveDays, days)], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adesao-npd-${days}dias-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!allowed) {
    return (
      <>
        <div className="topbar">
          <h1>Adesão de uso</h1>
        </div>
        <div className="content">
          <div className="card card-pad">
            <p className="muted">Apenas administradores podem ver a adesão de uso.</p>
          </div>
        </div>
      </>
    );
  }

  const statusBadge = (s: UsageStatus) =>
    s === 'nunca' ? (
      <span className="badge atrasada">Nunca acessou</span>
    ) : s === 'inativo' ? (
      <span className="badge atencao">Inativo {inactiveDays}+ dias</span>
    ) : (
      <span className="badge concluido">Ativo</span>
    );

  return (
    <>
      <div className="topbar">
        <h1>Adesão de uso</h1>
        <div className="stat-strip">
          <div className="stat-chip">
            <div className="n">{counts.ativo}</div>
            <div className="l">Ativos</div>
          </div>
          <div className={`stat-chip${counts.inativo ? ' warn' : ''}`}>
            <div className="n">{counts.inativo}</div>
            <div className="l">Inativos</div>
          </div>
          <div className={`stat-chip${counts.nunca ? ' warn' : ''}`}>
            <div className="n">{counts.nunca}</div>
            <div className="l">Nunca acessaram</div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="card card-pad" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="chip-toggle" role="group" aria-label="Período">
              {PERIODS.map((p) => (
                <button key={p} className={days === p ? 'on' : ''} onClick={() => setDays(p)}>
                  {p} dias
                </button>
              ))}
            </div>
            <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              Inativo após
              <select
                value={inactiveDays}
                onChange={(e) => setInactiveDays(Number(e.target.value))}
              >
                {INACTIVE_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} dias
                  </option>
                ))}
              </select>
            </label>
            <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              Mostrar
              <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
                <option value="todos">Todos</option>
                <option value="nunca">Nunca acessaram</option>
                <option value="inativo">Inativos</option>
                <option value="ativo">Ativos</option>
              </select>
            </label>
            <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={exportCsv}>
              Exportar CSV
            </button>
          </div>
          <p className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            Tempo logado = tempo com atividade na tela (sinal a cada minuto; pausas acima de 5
            minutos sem interação não contam). Ações = alterações em etapas/projetos, notas,
            aprovações de gate e mensagens de discussão registradas no período. O registro de
            uso começa quando a migração 0031 é aplicada; antes disso, só há as ações e o último
            login.
          </p>
        </div>

        {err && (
          <div className="card card-pad" style={{ marginBottom: 14 }}>
            <p style={{ color: 'var(--st-atrasada)' }}>
              Não foi possível carregar o relatório: {err}. Se a migração 0031 ainda não foi
              aplicada no Supabase, aplique-a primeiro (veja docs/PAINEL-ADESAO.md).
            </p>
          </div>
        )}

        <div className="card card-pad">
          {loading ? (
            <p className="muted">Carregando…</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Situação</th>
                    <th>Último acesso</th>
                    <th>Sessões</th>
                    <th>Dias com uso</th>
                    <th>Tempo logado</th>
                    <th>Ações</th>
                    <th>Detalhe das ações</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(({ r, status }) => (
                    <tr key={r.user_id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.full_name ?? r.email}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {r.email} · {ROLE_LABELS[r.role as AppRole] ?? r.role}
                        </div>
                      </td>
                      <td>{statusBadge(status)}</td>
                      <td>
                        {fmtDateTime(r.last_access ?? r.last_sign_in)}
                        {!r.last_access && r.last_sign_in && (
                          <div className="muted" style={{ fontSize: 11 }}>
                            (último login)
                          </div>
                        )}
                      </td>
                      <td>{r.sessions}</td>
                      <td>{r.active_days}</td>
                      <td>{fmtMinutes(r.minutes_logged)}</td>
                      <td style={{ fontWeight: 700 }}>{r.actions_total}</td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {r.actions_stages} etapas/projetos · {r.actions_notes} notas ·{' '}
                        {r.actions_gates} aprovações · {r.actions_messages} mensagens
                      </td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={8} className="muted">
                        Nenhum usuário neste filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
